import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import ProFeatureGate from '@/components/ProFeatureGate';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Plus, Wallet, TrendingUp, Building2, CreditCard, Trash2, Edit2, ChevronRight, IndianRupee, DollarSign } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine } from 'recharts';
import { autoBucketSeries, symmetricalDomain } from '@/lib/chartUtils';
import { toast } from 'sonner';
import { fmtMoney, getDisplayCurrency } from '@/lib/currency';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';

const ASSET_TYPES = ['Cash', 'Checking Account', 'Savings Account', 'Investment', 'TFSA', 'RRSP', 'Stocks', 'Crypto', 'Mutual Fund', 'ETF', 'Bonds', 'Vehicle', 'Real Estate', 'Other'];
const LIABILITY_TYPES = ['Credit Card', 'Student Loan', 'Auto Loan', 'Mortgage', 'Personal Loan', 'Other'];
const CURRENCIES = ['CAD', 'USD', 'INR', 'GBP', 'EUR', 'AUD'];
const CURRENCY_SYMBOLS = { USD: '$', INR: '\u20B9', CAD: 'C$', GBP: '\u00A3', EUR: '\u20AC', AUD: 'A$' };
const COLORS = ['hsl(136,18%,35%)', 'hsl(43,74%,50%)', 'hsl(8,52%,51%)', 'hsl(197,37%,44%)', 'hsl(280,45%,55%)', 'hsl(30,70%,50%)'];

// Aggregate values come from backend converted to CAD, but we label them in the
// user's chosen display currency for UI parity with Dashboard/Analytics (v1.4.9).
const fmtCad = (n) => fmtMoney(n);
const fmtCur = (n, cur = 'CAD') => {
  try { return new Intl.NumberFormat('en-CA', { style: 'currency', currency: cur, minimumFractionDigits: 0 }).format(n || 0); }
  catch { return `${CURRENCY_SYMBOLS[cur] || cur}${(n || 0).toLocaleString()}`; }
};
const fmt = fmtCad;

export default function NetWorthPage() {
  useDisplayCurrency(); // re-render when user changes base currency
  const { user } = useAuth();
  const defaultCurrency = user?.base_currency || 'CAD';
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [detailType, setDetailType] = useState(null);
  const [historyRange, setHistoryRange] = useState('1y');
  const [detailTxns, setDetailTxns] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, l, s, h] = await Promise.all([
        api.get('/api/networth/assets'), api.get('/api/networth/liabilities'),
        api.get('/api/networth/snapshot'), api.get(`/api/networth/history?range=${historyRange}`),
      ]);
      setAssets(a.data); setLiabilities(l.data); setSnapshot(s.data); setHistory(h.data);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [historyRange]);

  useEffect(() => { load(); }, [load]);

  const handleDeleteAsset = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete(`/api/networth/assets/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };
  const handleDeleteLiability = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete(`/api/networth/liabilities/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };

  const nw = snapshot || { total_assets: 0, total_liabilities: 0, net_worth: 0 };

  // Convert a CAD amount to the user's display currency using live FX rates from snapshot
  const convertFromCad = (cadAmount) => {
    const displayCur = getDisplayCurrency();
    if (displayCur === 'CAD') return cadAmount;
    const rate = (nw.exchange_rates || {})[displayCur];
    const result = (rate && rate > 0) ? cadAmount / rate : cadAmount;
    
    // Debug logging
    if (cadAmount > 0) {
      console.log(`[convertFromCad] CAD $${cadAmount} → ${displayCur} rate:${rate} → ${result}`);
    }
    
    return result;
  };

  // All chart/aggregate data converted to display currency
  // Transform to monthly data (1st of each month only)
  const monthlyChartData = useMemo(() => {
    const monthlyMap = new Map();
    
    // Filter to only include 1st of each month
    history.forEach((h) => {
      const date = new Date(h.date);
      const day = date.getDate();
      
      // Only include data from 1st of the month
      if (day === 1) {
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = date.toLocaleDateString('en-US', { month: 'short' });
        const value = Math.round(convertFromCad(h.net_worth));
        
        monthlyMap.set(monthKey, {
          date: h.date,
          monthKey,
          monthLabel,
          value
        });
      }
    });
    
    // Convert to array and sort by date
    const monthlyData = Array.from(monthlyMap.values()).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    // If no data on 1st, fill in with current year's months
    if (monthlyData.length === 0) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      
      // Generate YTD monthly data points (Jan to current month)
      for (let month = 0; month <= currentMonth; month++) {
        const monthDate = new Date(currentYear, month, 1);
        const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'short' });
        
        monthlyData.push({
          date: monthDate.toISOString().split('T')[0],
          monthKey: `${currentYear}-${String(month + 1).padStart(2, '0')}`,
          monthLabel,
          value: 0
        });
      }
    }
    
    return monthlyData;
  }, [history, convertFromCad]);

  const chartData = monthlyChartData;
  const bucketed = useMemo(() => ({ series: monthlyChartData, bucketSize: 'monthly' }), [monthlyChartData]);
  const yDomain = useMemo(() => symmetricalDomain(monthlyChartData.map((p) => p.value)), [monthlyChartData]);

  // Group by currency
  const assetsByCurrency = {};
  assets.forEach((a) => { const c = a.currency || 'USD'; if (!assetsByCurrency[c]) assetsByCurrency[c] = []; assetsByCurrency[c].push(a); });
  const liabilitiesByCurrency = {};
  liabilities.forEach((l) => { const c = l.currency || 'USD'; if (!liabilitiesByCurrency[c]) liabilitiesByCurrency[c] = []; liabilitiesByCurrency[c].push(l); });

  // Pie data — values converted to display currency
  const assetPie = Object.entries(nw.assets_breakdown || {}).map(([k, v]) => ({ name: k, value: Math.round(convertFromCad(v)) }));
  const liabPie = Object.entries(nw.liabilities_breakdown || {}).map(([k, v]) => ({ name: k, value: Math.round(convertFromCad(v)) }));

  // Display currency symbol for chart axis labels
  const displaySym = CURRENCY_SYMBOLS[getDisplayCurrency()] || '$';

  const openDetail = (item, type) => {
    setDetailItem(item);
    setDetailType(type);
    setDetailTxns(null);
    setDetailLoading(true);
    const ep = type === 'asset'
      ? `/api/networth/assets/${item._id}/transactions`
      : `/api/networth/liabilities/${item._id}/transactions`;
    api.get(ep)
      .then((r) => setDetailTxns(r.data))
      .catch(() => setDetailTxns({ transactions: [], count: 0 }))
      .finally(() => setDetailLoading(false));
  };

  const pageContent = (
    <div className="space-y-6" data-testid="networth-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Net Worth</h1>
          <p className="text-sm text-muted-foreground mt-1">Assets, liabilities &amp; investments</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAdd('asset')} data-testid="add-asset-btn"><Plus className="h-4 w-4 mr-1" />Add Asset</Button>
          <Button size="sm" onClick={() => setShowAdd('liability')} data-testid="add-liability-btn"><Plus className="h-4 w-4 mr-1" />Add Liability</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-border shadow-none cursor-pointer hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid="total-assets-card" onClick={() => setDetailType('assets-overview')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-green-600 dark:text-green-400" /><p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">Total Assets ({getDisplayCurrency()})</p></div>
            <p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums text-green-600 dark:text-green-400">{fmtMoney(convertFromCad(nw.total_assets))}</p>
            <p className="text-xs text-muted-foreground mt-1">{assets.length} items &middot; Click for details</p>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-none cursor-pointer hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid="total-liabilities-card" onClick={() => setDetailType('liabilities-overview')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><CreditCard className="h-4 w-4 text-red-500 dark:text-red-400" /><p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">Total Liabilities ({getDisplayCurrency()})</p></div>
            <p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums text-red-500 dark:text-red-400">{fmtMoney(convertFromCad(nw.total_liabilities))}</p>
            <p className="text-xs text-muted-foreground mt-1">{liabilities.length} items &middot; Click for details</p>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-none" data-testid="net-worth-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Wallet className="h-4 w-4 text-primary" /><p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">Net Worth ({getDisplayCurrency()})</p></div>
            <p className={`text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums ${nw.net_worth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>{fmtMoney(convertFromCad(nw.net_worth))}</p>
            {nw.exchange_rates && getDisplayCurrency() !== 'CAD' && (
                <p className="text-[10px] text-muted-foreground mt-1">1 CAD ≈ {fmtMoney(1 / (nw.exchange_rates[getDisplayCurrency()] || 1), getDisplayCurrency(), { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</p>
              )}
            {nw.exchange_rates && getDisplayCurrency() === 'CAD' && (
                <p className="text-[10px] text-muted-foreground mt-1">Rates: 1 USD = {(nw.exchange_rates?.USD || 0).toFixed(4)} CAD &middot; 1 INR = {(nw.exchange_rates?.INR || 0).toFixed(5)} CAD</p>
              )}
          </CardContent>
        </Card>
      </div>

      {/* Breakdown Pies + Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {assetPie.length > 0 && (
          <Card className="border border-border shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Assets Breakdown</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart><Pie data={assetPie} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" stroke="none">{assetPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} /></PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-1">{assetPie.map((d, i) => <div key={d.name} className="flex justify-between text-xs"><div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><span className="text-muted-foreground">{d.name}</span></div><span className="tabular-nums font-medium">{fmt(d.value)}</span></div>)}</div>
            </CardContent>
          </Card>
        )}
        {liabPie.length > 0 && (
          <Card className="border border-border shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Liabilities Breakdown</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart><Pie data={liabPie} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" stroke="none">{liabPie.map((_, i) => <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} /></PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-1">{liabPie.map((d, i) => <div key={d.name} className="flex justify-between text-xs"><div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-sm" style={{ backgroundColor: COLORS[(i + 2) % COLORS.length] }} /><span className="text-muted-foreground">{d.name}</span></div><span className="tabular-nums font-medium">{fmt(d.value)}</span></div>)}</div>
            </CardContent>
          </Card>
        )}
        {/* GRAPH TEMPORARILY DISABLED - P3 Task */}
        {false && bucketed.series.length >= 1 && (
          <Card className="border border-border shadow-none md:col-span-2 lg:col-span-3">
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-sm font-medium flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" />Net Worth Trend</CardTitle>
                <p className="text-[11px] text-muted-foreground mt-0.5">{bucketed.series.length} points · {bucketed.granularity} scale</p>
              </div>
              <div className="flex gap-1 p-1 rounded-md bg-muted/50" data-testid="networth-range-selector">
                {['ytd', '1y', '2y', '3y', '5y', 'all'].map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setHistoryRange(r)}
                    data-testid={`networth-range-${r}`}
                    className={`text-[11px] px-2 py-0.5 rounded transition-colors ${historyRange === r ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                  >{r.toUpperCase()}</button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={bucketed.series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="monthLabel" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis domain={yDomain} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v < 0 ? '-' : ''}${displaySym}${Math.abs(v) >= 1000 ? (Math.abs(v) / 1000).toFixed(0) + 'k' : Math.abs(v)}`} />
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} />
                  <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="value" name="Net Worth" stroke="hsl(136,18%,35%)" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
        {/* Coming Soon Card for Graph - Admin Only */}
        {user?.role === 'admin' && (
          <Card className="border border-border shadow-none md:col-span-2 lg:col-span-3 bg-muted/30">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <TrendingUp className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Net Worth History Graph</h3>
              <p className="text-sm text-muted-foreground text-center max-w-md">
                Historical trend visualization is coming soon! Track your assets and liabilities below.
              </p>
              <Badge variant="outline" className="mt-3 text-xs">Admin Preview</Badge>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="assets">
        <TabsList><TabsTrigger value="assets" data-testid="assets-tab">Assets ({assets.length})</TabsTrigger><TabsTrigger value="liabilities" data-testid="liabilities-tab">Liabilities ({liabilities.length})</TabsTrigger></TabsList>

        <TabsContent value="assets" className="mt-4">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-muted-foreground">Logged in {Object.keys(assetsByCurrency).join(', ') || 'USD'}</p>
            <Button size="sm" data-testid="add-asset-btn" onClick={() => { setEditItem(null); setShowAdd('asset'); }}><Plus className="h-4 w-4 mr-1" />Add Asset</Button>
          </div>
          {Object.entries(assetsByCurrency).map(([cur, items]) => (
            <div key={cur} className="mb-4">
              <div className="flex items-center gap-2 mb-2"><Badge variant="outline" className="text-xs">{cur === 'INR' ? <><IndianRupee className="h-3 w-3 mr-0.5" />{cur}</> : <><DollarSign className="h-3 w-3 mr-0.5" />{cur}</>}</Badge><span className="text-xs text-muted-foreground">{fmtCur(items.reduce((s, a) => s + a.value, 0), cur)}</span></div>
              <div className="space-y-2">{items.map((a) => (
                <div key={a._id} className="flex items-center justify-between p-3 rounded-md border border-border bg-card cursor-pointer hover:bg-accent/30 transition-colors" onClick={() => openDetail(a, 'asset')} data-testid={`asset-${a._id}`}>
                  <div className="min-w-0"><p className="text-sm font-medium">{a.name}</p><div className="flex items-center gap-2 mt-0.5"><Badge variant="secondary" className="text-xs">{a.asset_type}</Badge><Badge variant="outline" className="text-xs">{a.currency || 'USD'}</Badge></div></div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold tabular-nums text-green-600 dark:text-green-400">{fmtCur(a.value, a.currency || 'CAD')}</span>
                    {a.currency && a.currency !== getDisplayCurrency() && a.value_cad != null && <span className="text-xs text-muted-foreground tabular-nums">({fmtMoney(convertFromCad(a.value_cad), getDisplayCurrency())})</span>}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}</div>
            </div>
          ))}
          {assets.length === 0 && <Card className="border border-border shadow-none"><CardContent className="py-8 text-center text-sm text-muted-foreground">No assets. Add in USD, INR, or other currencies.</CardContent></Card>}
        </TabsContent>

        <TabsContent value="liabilities" className="mt-4">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-muted-foreground">Logged in {Object.keys(liabilitiesByCurrency).join(', ') || 'USD'}</p>
            <Button size="sm" data-testid="add-liability-btn" onClick={() => { setEditItem(null); setShowAdd('liability'); }}><Plus className="h-4 w-4 mr-1" />Add Liability</Button>
          </div>
          {Object.entries(liabilitiesByCurrency).map(([cur, items]) => (
            <div key={cur} className="mb-4">
              <div className="flex items-center gap-2 mb-2"><Badge variant="outline" className="text-xs">{cur === 'INR' ? <><IndianRupee className="h-3 w-3 mr-0.5" />{cur}</> : <><DollarSign className="h-3 w-3 mr-0.5" />{cur}</>}</Badge><span className="text-xs text-muted-foreground">{fmtCur(items.reduce((s, l) => s + l.amount, 0), cur)}</span></div>
              <div className="space-y-2">{items.map((l) => (
                <div key={l._id} className="flex items-center justify-between p-3 rounded-md border border-border bg-card cursor-pointer hover:bg-accent/30 transition-colors" onClick={() => openDetail(l, 'liability')} data-testid={`liability-${l._id}`}>
                  <div className="min-w-0"><p className="text-sm font-medium">{l.name}</p><div className="flex items-center gap-2 mt-0.5"><Badge variant="secondary" className="text-xs">{l.liability_type}</Badge><Badge variant="outline" className="text-xs">{l.currency || 'USD'}</Badge>{l.interest_rate && <span className="text-xs text-muted-foreground">{l.interest_rate}% APR</span>}</div></div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold tabular-nums text-red-500 dark:text-red-400">{fmtCur(l.amount, l.currency || 'CAD')}</span>
                    {l.currency && l.currency !== getDisplayCurrency() && l.amount_cad != null && <span className="text-xs text-muted-foreground tabular-nums">({fmtMoney(convertFromCad(l.amount_cad), getDisplayCurrency())})</span>}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}</div>
            </div>
          ))}
          {liabilities.length === 0 && <Card className="border border-border shadow-none"><CardContent className="py-8 text-center text-sm text-muted-foreground">No liabilities. Add in USD, INR, or other currencies.</CardContent></Card>}
        </TabsContent>
      </Tabs>

      {/* Edit/Add Dialogs — mounted outside Tabs so they work regardless of active tab (Item §8 fix) */}
      <Dialog open={showAdd === 'asset'} onOpenChange={(o) => { if (!o) { setShowAdd(null); setEditItem(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">{editItem ? 'Edit' : 'Add'} Asset</DialogTitle></DialogHeader>
          <ItemForm type="asset" item={editItem} defaultCurrency={defaultCurrency} onSave={async () => { await load(); setShowAdd(null); setEditItem(null); }} />
        </DialogContent>
      </Dialog>
      <Dialog open={showAdd === 'liability'} onOpenChange={(o) => { if (!o) { setShowAdd(null); setEditItem(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">{editItem ? 'Edit' : 'Add'} Liability</DialogTitle></DialogHeader>
          <ItemForm type="liability" item={editItem} defaultCurrency={defaultCurrency} onSave={async () => { await load(); setShowAdd(null); setEditItem(null); }} />
        </DialogContent>
      </Dialog>

      {/* Detail Sheet */}
      <Sheet open={!!detailItem} onOpenChange={(o) => { if (!o) { setDetailItem(null); setDetailType(null); setDetailTxns(null); } }}>
        <SheetContent className="w-[400px] sm:w-[480px] overflow-y-auto" data-testid="networth-detail-sheet">
          <SheetHeader><SheetTitle className="font-['Outfit']">{detailItem?.name}</SheetTitle></SheetHeader>
          {detailItem && (
            <div className="space-y-4 mt-4">
              <div className="p-4 rounded-md border border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-[0.15em] mb-1">{detailType === 'asset' ? 'Current Value' : 'Amount Owed'}</p>
                <p className={`text-3xl font-bold tracking-tight font-['Outfit'] tabular-nums ${detailType === 'asset' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                  {fmtCur(detailType === 'asset' ? detailItem.value : detailItem.amount, detailItem.currency || 'CAD')}
                </p>
                {detailItem.currency && detailItem.currency !== getDisplayCurrency() && (
                  <p className="text-sm text-muted-foreground mt-1">≈ {fmtMoney(convertFromCad(detailType === 'asset' ? detailItem.value_cad : detailItem.amount_cad), getDisplayCurrency())}</p>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Type</span><span className="font-medium">{detailType === 'asset' ? detailItem.asset_type : detailItem.liability_type}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Currency</span><Badge variant="outline">{detailItem.currency || 'USD'}</Badge></div>
                {detailItem.interest_rate && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Interest Rate</span><span className="font-medium">{detailItem.interest_rate}%</span></div>}
                {detailItem.notes && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Notes</span><span>{detailItem.notes}</span></div>}
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Added</span><span className="text-xs">{detailItem.created_at ? new Date(detailItem.created_at).toLocaleDateString() : 'N/A'}</span></div>
              </div>

              {/* Transaction log — Item §6 drill-down */}
              <div className="pt-2 border-t border-border" data-testid="detail-txn-log">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">Transaction Log</p>
                  {detailTxns && <Badge variant="secondary" className="text-[10px] h-4">{detailTxns.count} entries</Badge>}
                </div>
                {detailType === 'asset' && detailTxns && (
                  <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                    <div className="p-2 rounded border border-border bg-green-50 dark:bg-green-950/30">
                      <p className="text-muted-foreground">Contributions</p>
                      <p className="font-semibold tabular-nums text-green-600 dark:text-green-400">{fmtCur(detailTxns.total_contributions || 0, detailItem.currency || 'CAD')}</p>
                    </div>
                    <div className="p-2 rounded border border-border bg-red-50 dark:bg-red-950/30">
                      <p className="text-muted-foreground">Withdrawals</p>
                      <p className="font-semibold tabular-nums text-red-500 dark:text-red-400">{fmtCur(detailTxns.total_withdrawals || 0, detailItem.currency || 'CAD')}</p>
                    </div>
                  </div>
                )}
                {detailType === 'liability' && detailTxns && (
                  <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                    <div className="p-2 rounded border border-border bg-green-50 dark:bg-green-950/30">
                      <p className="text-muted-foreground">Repayments</p>
                      <p className="font-semibold tabular-nums text-green-600 dark:text-green-400">{fmtCur(detailTxns.total_repayments || 0, detailItem.currency || 'CAD')}</p>
                    </div>
                    <div className="p-2 rounded border border-border bg-red-50 dark:bg-red-950/30">
                      <p className="text-muted-foreground">Charges</p>
                      <p className="font-semibold tabular-nums text-red-500 dark:text-red-400">{fmtCur(detailTxns.total_charges || 0, detailItem.currency || 'CAD')}</p>
                    </div>
                  </div>
                )}
                {detailLoading && <p className="text-xs text-muted-foreground py-3 text-center">Loading transactions...</p>}
                {!detailLoading && detailTxns && detailTxns.transactions.length === 0 && (
                  <p className="text-xs text-muted-foreground py-3 text-center">No linked transactions yet. Tag a transaction to this {detailType} from the Transactions page.</p>
                )}
                {!detailLoading && detailTxns && detailTxns.transactions.length > 0 && (
                  <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
                    {detailTxns.transactions.map((t) => {
                      const isIncrease = detailType === 'asset'
                        ? (t.is_investment || t.transaction_type === 'credit')
                        : (t.transaction_type === 'debit' && !t.is_repayment);
                      const sign = isIncrease ? '+' : '-';
                      const color = (detailType === 'asset' ? isIncrease : !isIncrease)
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-500 dark:text-red-400';
                      const tag = t.is_investment ? 'SIP' : t.is_repayment ? 'Repayment' : t.transaction_type === 'credit' ? 'Credit' : 'Charge';
                      const destCcy = detailItem.currency || 'CAD';
                      const srcCcy = (t.original_currency || destCcy).toUpperCase();
                      const displayAmt = (t.converted_amount != null && srcCcy !== destCcy) ? t.converted_amount : t.amount;
                      const showOriginal = srcCcy !== destCcy && t.converted_amount != null;
                      return (
                        <div key={t._id} className="flex items-center justify-between text-xs p-2 rounded border border-border hover:bg-accent/30" data-testid={`detail-txn-${t._id}`}>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{t.description || t.merchant || 'Transaction'}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-muted-foreground">{t.date}</span>
                              <Badge variant="outline" className="text-[9px] h-4 px-1">{tag}</Badge>
                              {showOriginal && (
                                <span className="text-[9px] text-muted-foreground italic" title={`Original: ${srcCcy} ${t.amount}`}>{srcCcy} {t.amount}</span>
                              )}
                            </div>
                          </div>
                          <span className={`font-semibold tabular-nums shrink-0 ml-2 ${color}`} data-testid={`detail-txn-amount-${t._id}`}>{sign}{fmtCur(displayAmt, destCcy)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => { setEditItem(detailItem); setShowAdd(detailType === 'asset' ? 'asset' : 'liability'); setDetailItem(null); }} data-testid="detail-edit-btn">
                  <Edit2 className="h-3.5 w-3.5 mr-1.5" />Edit
                </Button>
                <Button variant="destructive" className="flex-1" onClick={() => {
                  if (detailType === 'asset') handleDeleteAsset(detailItem._id);
                  else handleDeleteLiability(detailItem._id);
                  setDetailItem(null);
                }} data-testid="detail-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete</Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Overview Sheets */}
      <Sheet open={detailType === 'assets-overview'} onOpenChange={(o) => { if (!o) setDetailType(null); }}>
        <SheetContent className="w-[400px] sm:w-[480px]">
          <SheetHeader><SheetTitle className="font-['Outfit']">All Assets</SheetTitle></SheetHeader>
          <div className="space-y-3 mt-4">
            {assets.map((a) => (
              <div key={a._id} className="flex justify-between items-center p-2 rounded border border-border cursor-pointer hover:bg-accent/30" onClick={() => { setDetailType(null); setTimeout(() => openDetail(a, 'asset'), 200); }}>
                <div><p className="text-sm font-medium">{a.name}</p><p className="text-xs text-muted-foreground">{a.asset_type} &middot; {a.currency || 'USD'}</p></div>
                <span className="text-sm font-semibold tabular-nums text-green-600 dark:text-green-400">{fmtCur(a.value, a.currency || 'CAD')}{a.currency !== getDisplayCurrency() && a.value_cad ? ` (${fmtMoney(convertFromCad(a.value_cad), getDisplayCurrency())})` : ''}</span>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
      <Sheet open={detailType === 'liabilities-overview'} onOpenChange={(o) => { if (!o) setDetailType(null); }}>
        <SheetContent className="w-[400px] sm:w-[480px]">
          <SheetHeader><SheetTitle className="font-['Outfit']">All Liabilities</SheetTitle></SheetHeader>
          <div className="space-y-3 mt-4">
            {liabilities.map((l) => (
              <div key={l._id} className="flex justify-between items-center p-2 rounded border border-border cursor-pointer hover:bg-accent/30" onClick={() => { setDetailType(null); setTimeout(() => openDetail(l, 'liability'), 200); }}>
                <div><p className="text-sm font-medium">{l.name}</p><p className="text-xs text-muted-foreground">{l.liability_type} &middot; {l.currency || 'USD'}{l.interest_rate ? ` &middot; ${l.interest_rate}%` : ''}</p></div>
                <span className="text-sm font-semibold tabular-nums text-red-500 dark:text-red-400">{fmtCur(l.amount, l.currency || 'CAD')}{l.currency !== getDisplayCurrency() && l.amount_cad ? ` (${fmtMoney(convertFromCad(l.amount_cad), getDisplayCurrency())})` : ''}</span>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );

  return (
    <ProFeatureGate feature="Net Worth Tracking">
      {pageContent}
    </ProFeatureGate>
  );
}

function ItemForm({ type, item, defaultCurrency = 'CAD', onSave }) {
  const isAsset = type === 'asset';
  const types = isAsset ? ASSET_TYPES : LIABILITY_TYPES;
  const typeField = isAsset ? 'asset_type' : 'liability_type';
  const valueField = isAsset ? 'value' : 'amount';
  const [form, setForm] = useState({
    name: item?.name || '', [typeField]: item?.[typeField] || types[0],
    [valueField]: (item?.[valueField])?.toString() || '', currency: item?.currency || defaultCurrency,
    notes: item?.notes || '', ...(isAsset ? {} : { interest_rate: item?.interest_rate?.toString() || '' }),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      if (isAsset) payload.value = parseFloat(payload.value);
      else { payload.amount = parseFloat(payload.amount); if (payload.interest_rate) payload.interest_rate = parseFloat(payload.interest_rate); else delete payload.interest_rate; }
      const ep = isAsset ? '/api/networth/assets' : '/api/networth/liabilities';
      if (item) await api.put(`${ep}/${item._id}`, payload); else await api.post(ep, payload);
      toast.success('Saved');
      await onSave();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="space-y-1"><Label className="text-xs">Name</Label><Input data-testid={`${type}-name-input`} value={form.name} onChange={(e) => set('name', e.target.value)} required /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Type</Label><Select value={form[typeField]} onValueChange={(v) => set(typeField, v)}><SelectTrigger data-testid={`${type}-type-select`}><SelectValue /></SelectTrigger><SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs">{isAsset ? 'Value' : 'Amount'}</Label><Input data-testid={`${type}-value-input`} type="number" step="0.01" value={form[valueField]} onChange={(e) => set(valueField, e.target.value)} required /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Currency</Label>
          <Select value={form.currency} onValueChange={(v) => set('currency', v)}>
            <SelectTrigger data-testid={`${type}-currency-select`}><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{CURRENCY_SYMBOLS[c] || c} {c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {!isAsset && <div className="space-y-1"><Label className="text-xs">Interest Rate (%)</Label><Input type="number" step="0.01" value={form.interest_rate} onChange={(e) => set('interest_rate', e.target.value)} /></div>}
      </div>
      <div className="space-y-1"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
      <Button type="submit" className="w-full" disabled={saving} data-testid={`${type}-submit-btn`}>{saving ? 'Saving...' : 'Save'}</Button>
    </form>
  );
}
